import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { useSurgeryQuery, useSurgerySchedulerSurgeonQuery } from "../../queries";
import { useMemo } from "react";
import { DateTimeInputs, LoadingSpinner, Select, StatefulForm, useStatefulForm } from "../../component-library";
import { Claims, Surgery } from "../../models";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { Lists } from "../lists";
import { usePermissions, useToasts } from "../../contexts";
import { getSurgeryScheduledOnUtc } from "../../helper-functions";

interface CreateSurgeryModalProps {
    show: boolean;
    onHide: () => void;
}

const CreateSurgerySchema = z.object({
    surgeonId: z.number().min(1, "Required"),
    surgeryDate: z.string().min(1, "Required"),
    surgeryTime: z.string().min(1, "Required"),
    surgeryTimeZone: z.string().min(1, "Required"),
});

export type CreateSurgeryType = z.infer<typeof CreateSurgerySchema>;

export function CreateSurgeryModal({ show, onHide }: Readonly<CreateSurgeryModalProps>) {
    const navigate = useNavigate();
    const { errorToast } = useToasts();
    const { hasPermission } = usePermissions();
    const canCreateSurgery = hasPermission(Claims.CreateSurgery);
    const {
        mySurgeonsQuery: { data, isFetching },
    } = useSurgerySchedulerSurgeonQuery({ queryType: "mySurgeons" });
    const {
        createMutation: { mutateAsync: createSurgery, isLoading },
    } = useSurgeryQuery();

    const surgeonOptions = data?.map((surgeon) => ({
        value: surgeon.id,
        label: `${surgeon.firstName} ${surgeon.lastName}`,
    }));

    const onSubmit = async (values: CreateSurgeryType) => {
        if (!canCreateSurgery) return;
        const surgery: Partial<Surgery> = {
            ...values,
            scheduledOnUtc: getSurgeryScheduledOnUtc(values),
        };
        try {
            const response = await createSurgery(surgery);
            const date = values.surgeryDate.split("T")[0];
            onHide();
            navigate(`/lists/${Lists.SurgeriesForDate}/${date}/surgeries/${response.id}`);
        } catch (error) {
            errorToast(error);
        }
    };

    const defaultValues: CreateSurgeryType = useMemo(
        () => ({
            surgeonId: 0,
            surgeryDate: "",
            surgeryTime: "",
            surgeryTimeZone: "",
        }),
        []
    );

    const { methods, isDirty } = useStatefulForm({ defaultValues, schema: CreateSurgerySchema });

    return (
        <Modal show={show} onHide={onHide} centered>
            <StatefulForm onSubmit={(values) => onSubmit(values as CreateSurgeryType)} methods={methods}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Surgery</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isFetching ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <div className="mb-2">
                                <Form.Label>
                                    Surgery Date/Time <span className="text-danger">*</span>
                                </Form.Label>
                                <InputGroup>
                                    <DateTimeInputs
                                        dateAccessor="surgeryDate"
                                        timeAccessor="surgeryTime"
                                        inGroup
                                        ignoreTimeZone
                                        required
                                    />
                                    <Select
                                        accessor="surgeryTimeZone"
                                        slug="time_zone"
                                        inGroup
                                        placeholder="time zone"
                                        required
                                    />
                                </InputGroup>
                            </div>
                            <div>
                                <Select accessor="surgeonId" options={surgeonOptions} label="Surgeon" required />
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={!isDirty || !canCreateSurgery || isLoading}>
                        {isLoading ? "Submitting..." : "Continue"}
                    </Button>
                </Modal.Footer>
            </StatefulForm>
        </Modal>
    );
}
